import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const ConnectWhatsApp = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState('initializing');
  const [error, setError] = useState('');
  const [isIndividualMode, setIsIndividualMode] = useState(false);
  const [lockedByOther, setLockedByOther] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const viewerIdRef = useRef(null);
  const isHolderRef = useRef(false);
  const heartbeatRef = useRef(null);
  const pollCancelRef = useRef(false);

  const getViewerId = () => {
    if (viewerIdRef.current) return viewerIdRef.current;
    const key = `qrViewerId:${locationId}:shared`;
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(key, id);
    }
    viewerIdRef.current = id;
    return id;
  };

  const lockBody = () => ({ locationId, viewerId: getViewerId() });

  const postLock = async (path) => {
    const resp = await fetch(`/whatsapp/qr-viewer/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lockBody()),
    });
    return resp.ok ? resp.json() : null;
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      if (!isHolderRef.current) return;
      const data = await postLock('heartbeat');
      if (data?.locked) {
        isHolderRef.current = false;
        pollCancelRef.current = true;
        stopHeartbeat();
        setLockedByOther(true);
        setStatus('locked');
      }
    }, 10000);
  };

  useEffect(() => {
    if (locationId) {
      checkLocationMode();
    }
    return () => {
      pollCancelRef.current = true;
      stopHeartbeat();
      if (isHolderRef.current && locationId && viewerIdRef.current) {
        try {
          navigator.sendBeacon(
            '/whatsapp/qr-viewer/release',
            new Blob([JSON.stringify(lockBody())], { type: 'application/json' }),
          );
        } catch (e) { /* ignore */ }
      }
    };
  }, [locationId]);

  const checkLocationMode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const statusResponse = await fetch(`/whatsapp/status/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        
        if (statusData.sharingMode === 'individual') {
          setIsIndividualMode(true);
          setStatus('individual_mode');
          return;
        }
        
        if (statusData.connected && statusData.ready) {
          setStatus('connected');
          navigate('/dashboard/subaccounts?connected=true');
          return;
        }
      }

      await claimAndStart();
    } catch (err) {
      console.error('Failed to check location mode:', err);
      setError('Failed to load location settings');
      setStatus('error');
    }
  };

  const claimAndStart = async () => {
    const claim = await postLock('claim');
    if (claim?.holder) {
      isHolderRef.current = true;
      setLockedByOther(false);
      startHeartbeat();
      initializeWhatsApp();
    } else {
      isHolderRef.current = false;
      setLockedByOther(true);
      setStatus('locked');
    }
  };

  const handleUseHere = async () => {
    setLockBusy(true);
    try {
      const data = await postLock('takeover');
      if (data?.holder) {
        isHolderRef.current = true;
        pollCancelRef.current = false;
        setLockedByOther(false);
        startHeartbeat();
        setStatus('initializing');
        initializeWhatsApp();
      }
    } finally {
      setLockBusy(false);
    }
  };

  const initializeWhatsApp = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const statusResponse = await fetch(`/whatsapp/status/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        if (statusData.connected && statusData.ready) {
          setStatus('connected');
          navigate('/dashboard/subaccounts?connected=true');
          return;
        }
      }

      setStatus('generating_qr');
      pollForQR();
    } catch (err) {
      console.error('Failed to check WhatsApp status:', err);
      setError('Failed to load WhatsApp connection');
      setStatus('error');
    }
  };

  const pollForQR = async () => {
    let attempts = 0;
    const maxAttempts = 30;
    pollCancelRef.current = false;
    
    const poll = async () => {
      if (pollCancelRef.current || !isHolderRef.current) return;
      try {
        const token = localStorage.getItem('access_token');
        const viewerId = getViewerId();
        const response = await fetch(`/whatsapp/qr/${locationId}?viewerId=${encodeURIComponent(viewerId)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();

          if (data.locked) {
            isHolderRef.current = false;
            stopHeartbeat();
            setLockedByOther(true);
            setStatus('locked');
            return;
          }
          
          if (data.ready) {
            setStatus('connected');
            setTimeout(() => {
              navigate('/dashboard/subaccounts?connected=true');
            }, 2000);
            return;
          }
          
          if (data.qrCode) {
            setQrCode(data.qrCode);
            setStatus('scan_qr');
            
            setTimeout(() => {
              pollForConnection();
            }, 3000);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setError('QR code generation timed out');
          setStatus('error');
        }
      } catch (err) {
        console.error('Error polling for QR:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setError('Failed to generate QR code');
          setStatus('error');
        }
      }
    };

    poll();
  };

  const pollForConnection = async () => {
    let attempts = 0;
    const maxAttempts = 60;
    pollCancelRef.current = false;

    const poll = async () => {
      if (pollCancelRef.current || !isHolderRef.current) return;
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`/whatsapp/status/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.connected && data.ready) {
            setStatus('connected');
            setTimeout(() => {
              navigate('/dashboard/subaccounts?connected=true');
            }, 2000);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setStatus('generating_qr');
          pollForQR();
        }
      } catch (err) {
        console.error('Error polling for connection:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        }
      }
    };

    poll();
  };

  const retryConnection = () => {
    setQrCode(null);
    setError('');
    setStatus('initializing');
    claimAndStart();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-4xl mb-4">
              📱
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Connect WhatsApp
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Location ID: {locationId}
            </p>

            {status === 'initializing' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Initializing WhatsApp client...</p>
              </div>
            )}

            {status === 'locked' && (
              <div className="text-center">
                <div className="text-6xl mb-4">🖥️</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  QR page is open in another window
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Close the other tab, or click <strong>Use here</strong> to continue on this device.
                  Switching may interrupt an in-progress scan.
                </p>
                <button
                  onClick={handleUseHere}
                  disabled={lockBusy}
                  className="btn-primary"
                >
                  {lockBusy ? 'Taking over...' : 'Use here'}
                </button>
              </div>
            )}

            {status === 'individual_mode' && (
              <div className="text-center">
                <div className="text-6xl mb-4">👥</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Individual Mode
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 font-medium mb-2">INDIVIDUAL MODE</p>
                  <p className="text-sm text-blue-700">
                    This location uses Individual Mode. Each user has their own WhatsApp connection.
                  </p>
                </div>
                <p className="text-gray-600 mb-4">
                  Please access your QR code from the User Management page.
                </p>
                <button
                  onClick={() => navigate(`/user-management/${locationId}`)}
                  className="btn-primary"
                >
                  Go to User Management
                </button>
              </div>
            )}

            {status === 'generating_qr' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Generating QR code...</p>
              </div>
            )}

            {status === 'scan_qr' && qrCode && (
              <div className="text-center">
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <img 
                    src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                    alt="WhatsApp QR Code" 
                    className="mx-auto max-w-xs"
                  />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Scan with WhatsApp
                </h3>
                <div className="text-left space-y-2 text-sm text-gray-600 mb-6">
                  <div className="flex items-start">
                    <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">1</span>
                    <span>Open WhatsApp on your phone</span>
                  </div>
                  <div className="flex items-start">
                    <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">2</span>
                    <span>Go to Settings → Linked Devices</span>
                  </div>
                  <div className="flex items-start">
                    <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">3</span>
                    <span>Tap "Link a Device"</span>
                  </div>
                  <div className="flex items-start">
                    <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">4</span>
                    <span>Scan this QR code</span>
                  </div>
                </div>
                <div className="animate-pulse">
                  <p className="text-green-600 font-medium">Waiting for scan...</p>
                </div>
              </div>
            )}

            {status === 'connected' && (
              <div className="text-center">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-lg font-medium text-green-600 mb-2">
                  Connected Successfully!
                </h3>
                <p className="text-gray-600 mb-4">
                  WhatsApp is now connected to this location.
                </p>
                <p className="text-sm text-gray-500">
                  Redirecting to dashboard...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <div className="text-6xl mb-4">❌</div>
                <h3 className="text-lg font-medium text-red-600 mb-2">
                  Connection Failed
                </h3>
                <p className="text-gray-600 mb-4">
                  {error || 'An unexpected error occurred'}
                </p>
                <button
                  onClick={retryConnection}
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/dashboard/subaccounts')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to Subaccounts
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectWhatsApp;
